import api from "services/api";
import { IContract } from "v2/pages/Workspace/WorkspaceListContracts/ListContracts/ListContracts.types";

export const ALL_WORKSPACES_ID = "ALL";

export interface IInsightsWorkspaceOption {
  id: string;
  label: string;
  buyerName: string;
  buyerOrganizationId: string;
  buyerFederalTaxId: string;
}

export interface IInsightsAgentRequest {
  content: string;
  workspace_id: string;
  session_id: string;
}

export interface IInsightsContentBlock {
  type: "text" | "table";
  text?: string;
  rows?: Array<Record<string, any>>;
  label?: string;
}

export interface IInsightsAgentResponse {
  reply: string;
  blocks?: IInsightsContentBlock[];
  session_id: string;
  message_count: number;
}

export interface IInsightsPrefabParams {
  cpf?: string;
  dias_min?: number;
}

export interface IInsightsPrefabResponse {
  action: string;
  rows: Array<Record<string, any>>;
  message?: string;
}

export type IInsightsPrefabKind =
  | "pendencias"
  | "investigar-cpf"
  | "performance"
  | "anomalias"
  | "exportacao"
  | "resumo-executivo";

/**
 * Não há endpoint dedicado de listagem para o Insights — reaproveita a mesma
 * rota que WorkspaceListContracts usa para listar os contratos (workspaces)
 * de um fornecedor com o comprador logado.
 */
export async function getWorkspacesForSeller(
  sellerOrganizationId: string
): Promise<IInsightsWorkspaceOption[]> {
  const response = await api.get<{ data: IContract[] }>(
    `/v2/organizations/${sellerOrganizationId}/workspaces`
  );
  const contracts = response.data.data ?? [];

  return contracts.map((contract) => ({
    id: contract.id,
    label: [
      contract.product?.name,
      contract.supplierCorporateName,
      contract.policyContractNumber ? `#${contract.policyContractNumber}` : null,
    ]
      .filter(Boolean)
      .join(" — "),
    buyerName: contract.buyerOrganization?.corporateName ?? "",
    buyerOrganizationId: contract.buyerOrganization?.id ?? "",
    buyerFederalTaxId: contract.buyerOrganization?.federalTaxId ?? "",
  }));
}

/**
 * Fallback dev-only: em ambiente local o backend precisa de
 * `snowflake_key.pem` (chave privada de autenticação JWT com o Snowflake)
 * pra chamar o Cortex Agent de verdade. Sem essa chave, /insights/agent
 * sempre responde 503 — o que é o comportamento correto, não um bug.
 * Enquanto a chave real não está disponível, isso deixa dar preview do
 * layout de conversa multi-turn localmente. `import.meta.env.DEV` garante
 * que o Vite elimina esse caminho inteiro do build de produção (mesmo
 * princípio do devPreview.ts do PlanExtensionFormulary).
 * TODO: remover assim que snowflake_key.pem estiver configurado localmente.
 */
const DEV_MOCK_SESSION_COUNTS = new Map<string, number>();

const DEV_MOCK_REPLIES = [
  "Encontrei 3 pendências de aprovação neste workspace: 2 inclusões e 1 exclusão aguardando há mais de 5 dias.",
  "A última execução processou 128 beneficiários, com 4 inconsistências cadastrais — a maioria por CPF duplicado.",
  "Comparando com o mês anterior, o volume de movimentações caiu 12%, principalmente por menos admissões em julho.",
  "Não encontrei anomalias críticas nas últimas 24h. Só um pico de exclusões no dia 28, dentro do esperado pelo calendário de fechamento.",
  "Resumo executivo: 1.240 vidas ativas, 96% de cobertura documental e 3 contratos com renovação prevista pros próximos 60 dias.",
];

function getDevMockAgentResponse(
  request: IInsightsAgentRequest
): IInsightsAgentResponse {
  const messageCount = (DEV_MOCK_SESSION_COUNTS.get(request.session_id) ?? 0) + 1;
  DEV_MOCK_SESSION_COUNTS.set(request.session_id, messageCount);

  const mockText = `[mock local — sem snowflake_key.pem] ${DEV_MOCK_REPLIES[(messageCount - 1) % DEV_MOCK_REPLIES.length]}`;

  return {
    reply: mockText,
    blocks: [{ type: "text", text: mockText }],
    session_id: request.session_id,
    message_count: messageCount,
  };
}

export async function sendInsightsAgentMessage(
  request: IInsightsAgentRequest,
  signal?: AbortSignal
): Promise<IInsightsAgentResponse> {
  try {
    const response = await api.post<IInsightsAgentResponse>(
      "/insights/agent",
      request,
      { signal }
    );
    return response.data;
  } catch (error: any) {
    if (import.meta.env.DEV && error?.response?.status === 503) {
      return getDevMockAgentResponse(request);
    }
    throw error;
  }
}

async function callInsightsPrefab(
  kind: IInsightsPrefabKind,
  workspaceId: string,
  params?: IInsightsPrefabParams
): Promise<IInsightsPrefabResponse> {
  const response = await api.post<IInsightsPrefabResponse>(
    `/insights/prefab/${kind}`,
    { workspace_id: workspaceId, params }
  );
  return response.data;
}

export const getPendenciasDoDia = (workspaceId: string) =>
  callInsightsPrefab("pendencias", workspaceId);

export const investigarCpf = (workspaceId: string, cpf: string) =>
  callInsightsPrefab("investigar-cpf", workspaceId, { cpf });

export const getPerformance = (workspaceId: string) =>
  callInsightsPrefab("performance", workspaceId);

export const getAnomalias = (workspaceId: string) =>
  callInsightsPrefab("anomalias", workspaceId);

export const getUltimaExportacao = (workspaceId: string) =>
  callInsightsPrefab("exportacao", workspaceId);

export const getResumoExecutivo = (workspaceId: string) =>
  callInsightsPrefab("resumo-executivo", workspaceId);

export type InsightsReportType =
  | "pendencias"
  | "performance"
  | "executivo"
  | "timeline";

export interface IInsightsReportRequest {
  workspace_id: string;
  cpfs?: string[];
}

export interface IInsightsReportResponse {
  html: string;
  filename: string;
  title: string;
}

/**
 * Mesmo racional do DEV_MOCK_REPLIES acima: localmente, /insights/report/:type
 * ainda não existe no backend (só /enabled, /agent e /prefab/:action estão
 * registrados em routes/insights/index.ts), então a chamada real vira 404 hoje
 * e viraria 503 se a rota existir mas depender de credencial Snowflake não
 * configurada localmente — tratamos os dois como "sem backend disponível" só
 * em dev.
 * TODO: remover o fallback de 404 assim que o backend publicar a rota.
 */
const DEV_MOCK_REPORT_TITLES: Record<InsightsReportType, string> = {
  pendencias: "Relatório de Pendências",
  performance: "Relatório de Performance",
  executivo: "Resumo Executivo",
  timeline: "Timeline do CPF",
};

function getDevMockReportResponse(
  type: InsightsReportType,
  request: IInsightsReportRequest
): IInsightsReportResponse {
  const title = DEV_MOCK_REPORT_TITLES[type];
  const scope = request.cpfs?.length
    ? `cpfs: ${request.cpfs.join(", ")}`
    : `workspace_id: ${request.workspace_id}`;

  return {
    title,
    filename: `relatorio-${type}-mock.html`,
    html: `<html><head><meta charset="utf-8"><title>${title}</title></head><body><h1>${title}</h1><p>[mock local — sem backend /insights/report/${type}] ${scope}</p></body></html>`,
  };
}

export async function generateInsightsReport(
  type: InsightsReportType,
  request: IInsightsReportRequest
): Promise<IInsightsReportResponse> {
  try {
    const response = await api.post<IInsightsReportResponse>(
      `/insights/report/${type}`,
      request
    );
    return response.data;
  } catch (error: any) {
    const status = error?.response?.status;
    if (import.meta.env.DEV && (status === 503 || status === 404)) {
      return getDevMockReportResponse(type, request);
    }
    throw error;
  }
}

export function getInsightsErrorMessage(error: any, fallback: string): string {
  return error?.response?.data?.message || fallback;
}

export interface IInsightsEnabledResponse {
  enabled: boolean;
  qualifyingBuyerIds: string[];
}

/**
 * Público (sem auth) — o gate é por conta Snowflake, não por sessão de
 * usuário, então dá pra decidir se mostra o item de sidebar/FAB/rota antes
 * de qualquer chamada autenticada.
 *
 * `qualifying_buyer_ids` lista os buyers cujos dados existem no Snowflake —
 * usado para filtrar a listagem de workspaces do seller (que por padrão
 * traz todos os buyers da corretora, mesmo os sem dado).
 */
export async function getInsightsEnabled(
  organizationId: string
): Promise<IInsightsEnabledResponse> {
  const response = await api.get<{
    enabled: boolean;
    qualifying_buyer_ids?: string[];
  }>("/insights/enabled", {
    params: { organization_id: organizationId },
  });
  return {
    enabled: Boolean(response.data.enabled),
    qualifyingBuyerIds: response.data.qualifying_buyer_ids ?? [],
  };
}
